import type { DOMAttributes, MouseEvent, ReactNode } from 'react';

import Link from '@shared/router/link';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

type CardEventHandlers = {
	[K in keyof DOMAttributes<HTMLElement> as K extends `on${string}`
		? K
		: never]?: DOMAttributes<HTMLElement>[K];
};

interface CardDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

export type CardVariant = 'default' | 'frame';

const DefaultCardVariant: CardVariant = 'default' as const;

interface CardProperties extends CardEventHandlers, CardDataAttributes {
	/** `frame` paints the table frame's border and surface, for a card that holds a table's row. */
	variant?: CardVariant;

	/** Makes the whole card a link, through the router. */
	href?: string;
	/** Without an `href`, makes the whole card a button. */
	onClick?: (event: MouseEvent<HTMLElement>) => void;

	children: ReactNode;
}

/**
 *	The surface a card is drawn on: border, background, corners, and the
 *	hover, pressed and focus looks. What goes inside is the caller's, so a
 *	post card or a comment card is a composition that wraps its content in
 *	this.
 *
 *	The element follows what the card does. `href` makes it the router's
 *	link, `onClick` a button, and neither a plain box with no states, since a
 *	card that does nothing should not look like it does.
 */
export function Card({
	variant = DefaultCardVariant,

	href,
	onClick,

	children,

	...props
}: CardProperties): ReactNode {
	const className: string = cn(
		css.card,
		css[variant],
		(href || onClick) && css.interactive
	);

	if (href) {
		return (
			<Link {...props} href={href} onClick={onClick} className={className}>
				{children}
			</Link>
		);
	}

	if (onClick) {
		return (
			<button {...props} type="button" onClick={onClick} className={className}>
				{children}
			</button>
		);
	}

	return (
		<div {...props} className={className}>
			{children}
		</div>
	);
}
